import {
  AsyncComboBox,
  type AsyncComboBoxProps,
} from '@/components/AsyncComboBox';
import { useField } from 'formik';
import { getFormFieldClasses } from '@/utility/utility';
import { FieldWrapper, type FieldWrapperConfig } from './FieldWrapper';

/**
 * Props for the AsyncComboBoxField component
 */
interface AsyncComboBoxFieldProps<TParams, TData>
  extends
    Omit<AsyncComboBoxProps<TParams, TData>, 'value' | 'onChange'>,
    FieldWrapperConfig {
  onChange?: (value: unknown) => void;
}

/**
 * A form field component that renders an AsyncComboBox.
 *
 * It automatically handles its own width based on the user-provided width
 * and the measured width of its parent element.
 *
 */
export function AsyncComboBoxField<TParams extends object | undefined, TData>({
  name,
  label,
  width,
  infoText,
  className = '',
  showErrorSpace,
  disabled = false,
  fieldClassName = '',
  labelHint,
  labelButton,
  onChange,
  ...props
}: AsyncComboBoxFieldProps<TParams, TData> & { onAddNew?: () => void }) {
  const [field, meta, helpers] = useField(name);

  const wrapperProps = {
    name,
    disabled,
    label,
    errorMeta: meta,
    fieldClassName,
    infoText,
    labelHint,
    showErrorSpace,
    labelButton,
  };

  return (
    <FieldWrapper {...wrapperProps}>
      <AsyncComboBox
        {...props}
        width={width}
        value={field.value}
        disabled={disabled}
        className={getFormFieldClasses({ meta, disabled, className })}
        error={meta.touched && meta.error ? meta.error : undefined}
        onChange={(val: unknown) => {
          const normalized = val === undefined || val === '' ? null : val;
          helpers.setValue(normalized);
          helpers.setTouched(true, false);
          onChange?.(normalized);
        }}
      />
    </FieldWrapper>
  );
}
